import React, { useState, useEffect, useCallback } from "react";
import emailjs from "@emailjs/browser";
import Button from "../../src/Components/Button";

/* ------------------- Inline TxtInput ------------------- */
const TxtInput = ({ label, value, onChange, onBlur, error, touched, type = "text" }) => {
  const id = label.replace(/\s+/g, "");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px", width: "100%" }}>
      <label
        htmlFor={id}
        style={{
          fontSize: "15px",
          fontWeight: 500,
          color: "#0D171C",
          fontFamily: "'Public Sans', sans-serif",
        }}
      >
        {label}
      </label>

      <input
        id={id}
        type={type}
        placeholder={`Your ${label.toLowerCase()}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        style={{
          padding: "14px 16px",
          fontSize: "16px",
          borderRadius: "8px",
          border: touched && error ? "1.5px solid #e74c3c" : "1.5px solid #d1d9dc",
          backgroundColor: "#f8fbfc",
          outline: "none",
          transition: "all 0.2s ease",
          fontFamily: "'Public Sans', sans-serif",
          boxShadow: touched && error ? "0 0 0 3px rgba(231, 76, 60, 0.1)" : "none",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "#4F8594";
          e.target.style.boxShadow = "0 0 0 3px rgba(79, 133, 148, 0.15)";
        }}
      />

      {touched && error && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "13px",
            color: "#e74c3c",
            marginTop: "4px",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

/* ------------------- Main Contactus Component ------------------- */
export default function Contactus() {
  const fields = ["Name", "Email", "Mobile Number", "Subject", "Message"];

  const initialForm = {
    Name: "",
    Email: "",
    PhoneNumber: "",
    Subject: "",
    Message: "",
  };

  const [formdata, setFormdata] = useState(initialForm);
  const [touched, setTouched] = useState({
    Name: false,
    Email: false,
    PhoneNumber: false,
    Subject: false,
    Message: false,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = useCallback(() => {
    const newErrors = {};

    if (!formdata.Name.trim()) newErrors.Name = "Name is required";

    if (!formdata.Email.trim()) {
      newErrors.Email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formdata.Email)) {
      newErrors.Email = "Enter a valid email address";
    }

    if (!formdata.PhoneNumber.trim()) {
      newErrors.PhoneNumber = "Mobile Number is required";
    } else {
      const digits = formdata.PhoneNumber.replace(/\D/g, "");
      if (digits.length < 10 || digits.length > 15) newErrors.PhoneNumber = "Enter a valid Mobile Number";
    }

    if (!formdata.Subject.trim()) newErrors.Subject = "Subject is required";
    if (!formdata.Message.trim()) newErrors.Message = "Message is required";

    return newErrors;
  }, [formdata]);

  useEffect(() => setErrors(validate()), [formdata, validate]);

  const handleChange = (field, value) => {
    setFormdata((prev) => ({ ...prev, [field]: value }));
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const allTouched = Object.keys(formdata).reduce((acc, k) => ({ ...acc, [k]: true }), {});
    setTouched(allTouched);

    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) return;

    setIsSubmitting(true);
    try {
      await emailjs.send(
        "service_39xtma1",
        "template_5mk9caj",
        {
          name: formdata.Name,
          email: formdata.Email,
          phone: formdata.PhoneNumber,
          subject: formdata.Subject,
          message: formdata.Message,
        },
        "qGSgh5WxJjk_uEGuE"
      );

      alert("Your message has been sent successfully!");
      setFormdata(initialForm);
      setTouched(Object.keys(initialForm).reduce((acc, k) => ({ ...acc, [k]: false }), {}));
    } catch (err) {
      console.error(err);
      alert("Failed to send the message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    emailjs.init("qGSgh5WxJjk_uEGuE");
  }, []);

  const isFormValid = Object.keys(errors).length === 0;

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "4rem auto",
        padding: "0 1.5rem",
        fontFamily: "'Public Sans', sans-serif",
        color: "#0D171C",
      }}
    >
      {/* ========== Contact Us Header with Green Box ========== */}
      <div
        style={{
          background: "linear-gradient(135deg, #E6F4F1 0%, #F0FFF4 100%)",
          padding: "2.5rem 2rem",
          borderRadius: "8px",
          border: "2px solid #14B88F20",
          textAlign: "center",
          marginBottom: "3rem",
        }}
      >
        <h1
          style={{
            fontSize: "2.5rem",
            fontWeight: "bold",
            margin: 0,
            fontFamily: "'Public Sans', sans-serif",
            color: "#14B88F",
          }}
        >
          Contact Us
        </h1>
      </div>

      {/* ========== Main Grid: Form + Info ========== */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "3rem",
          alignItems: "start",
        }}
      >
        {/* ========== Left: Form ========== */}
        <form
          onSubmit={handleSubmit}
          noValidate
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
            padding: "2rem",
            borderRadius: "8px",
          }}
        >
          {fields.map((field) => {
            const key = field.replace(/\s+/g, "");
            return (
              <TxtInput
                key={field}
                label={field}
                type={field === "Email" ? "email" : field === "Mobile Number" ? "tel" : "text"}
                value={formdata[key]}
                onChange={(v) => handleChange(key, v)}
                onBlur={() => handleBlur(key)}
                error={errors[key]}
                touched={touched[key]}
              />
            );
          })}
<div style={{ marginTop: "1rem", color: '#fff' }}>
          <Button
            buttonname={isSubmitting ? "Sending…" : "Submit"}
            disabled={!isFormValid || isSubmitting}
            style={{
              width: "100%",
              padding: "16px 20px",
              fontSize: "16px",
              fontWeight: 600,
              background: isFormValid && !isSubmitting
                ? "linear-gradient(135deg, #14B88F, #0D9488)"
                : "#a0b4ba",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              cursor: isFormValid && !isSubmitting ? "pointer" : "not-allowed",
              transition: "all 0.3s ease",
              boxShadow: isFormValid && !isSubmitting
                ? "0 6px 16px rgba(20, 184, 143, 0.3)"
                : "none",
            }}
            onMouseEnter={(e) => {
              if (isFormValid && !isSubmitting) {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 8px 20px rgba(20, 184, 143, 0.35)";
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = isFormValid && !isSubmitting
                ? "0 6px 16px rgba(20, 184, 143, 0.3)"
                : "none";
            }}
          />
          </div>
        </form>

        

        {/* ========== Right: Get in Touch + Address (No Box) ========== */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: "3.4rem 1.5rem",
          }}
        >
          <h2
            style={{
              fontSize: "1.5rem",
              fontWeight: "bold",
              margin: "0 0 1rem 0",
              fontFamily: "'Public Sans', sans-serif",
              color: "#0D171C",
            }}
          >
            Get in Touch
          </h2>
          <p
            style={{
              fontSize: "1rem",
              color: "#4F8594",
              lineHeight: 1.7,
              margin: "0 0 2rem 0",
            }}
          >
            We're here to help you navigate your sustainability journey. Reach out with any questions or to discuss how we can support your goals.
          </p>

          {/* Address without any box */}
          <div style={{ marginTop: "1rem" }}>
            <p
              style={{
                fontSize: "1rem",
                color: "#0D171C",
                margin: "0 0 0.5rem 0",
                fontWeight: 700,
              }}
            >
              Address
            </p>
            <p
              style={{
                 fontSize: "1rem",
              color: "#4F8594",
              lineHeight: 1.7,
              margin: "0 0 2rem 0",
              }}
            >
              142 Hillside Road, Great Bar,<br />
              Birmingham B43, 6NQ UK
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}